import { Avatar, Badge, Box, Button, Card, CardBody, CardHeader, CircularProgress, CircularProgressLabel, Flex, Grid, Hide, Icon, Image, Progress, Select, SimpleGrid, Skeleton, Spacer, Stack, Stat, StatHelpText, StatLabel, StatNumber, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, useToast } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { BsArrowRight, BsPersonVcardFill } from 'react-icons/bs';
import { FaUserTie } from 'react-icons/fa';
import { FaUserGroup } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import IconBox from '../../components/Icons/IconBox';
import { CartIcon, DocumentIcon, GlobeIcon, RocketIcon, StatsIcon, WalletIcon } from '../../components/Icons/Icons.js';
import { IoCheckmarkDoneCircleSharp, IoDocument, IoEllipsisHorizontal } from 'react-icons/io5';
import { BiHappy } from 'react-icons/bi';
import { LuPackageX } from "react-icons/lu";
import { TbTruckDelivery, TbTruckReturn } from "react-icons/tb";
import empty from '../../assets/images/empty.png';


import LineChart from '../../components/Charts/LineChart';
import axiosClient, { linkImage } from '../../axios-client.js';
import { useStateContext } from '../../context/ContextProvider.jsx';
import { Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";

import { MdOutlineAddShoppingCart, MdTrendingUp, MdOutlineBarChart } from 'react-icons/md';
import SimplePagination from '../../components/SimplePagination.jsx';
import MyPagination from '../../components/MyPagination.jsx';
import FormattedNumber, { formatNumber } from '../../components/FormattedNumber';
import { Tooltip as ChakraTooltip, Spinner } from '@chakra-ui/react';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

/* ─── Global CSS injected once ─────────────────────────────────────────────── */
const GLOBAL_CSS = `
.stats-root { font-family: 'Inter', sans-serif; }

.glass-card {
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(0,0,0,0.04);
}

.gradient-text {
  background: linear-gradient(135deg, #7B5EA7 0%, #6DD5FA 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.metric-card {
  transition: transform .22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow .22s cubic-bezier(0.4, 0, 0.2, 1);
  cursor:default;
}
.metric-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(123,94,167,.12);
}

.floating-icon { animation: float 3s ease-in-out infinite; }
`;

/* ─── Sub-components ─────────────────────────────────────────────────────────  */

const GlassMetricCard = ({ label, value, sub, icon: IconComp, gradient, badge, tooltip, delay = 0, isDark }) => {
	const card = (
		<Box
			className="metric-card animate-fade-up glass-card"
			style={{ animationDelay: `${delay}ms` }}
			bg={isDark ? 'rgba(26,32,44,0.75)' : 'rgba(255,255,255,0.9)'}
			borderRadius="16px"
			p={5}
			position="relative"
			overflow="hidden"
			h="100%"
			border="1px solid"
			borderColor={isDark ? 'whiteAlpha.100' : 'gray.100'}
			boxShadow={isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)'}
		>
			<Box
				position="absolute" top="-20px" right="-20px"
				w="60px" h="60px" borderRadius="50%"
				bg={gradient} opacity={0.2} filter="blur(14px)"
			/>
			<Flex justify="space-between" align="flex-start" h="100%">
				<Flex direction="column" gap={1} flex={1}>
					<Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="1px"
						color={isDark ? 'whiteAlpha.500' : 'gray.400'}>
						{label}
					</Text>
					<Text fontSize="20px" fontWeight="800" lineHeight={1.1}
						color={isDark ? 'whiteAlpha.900' : 'gray.800'}>
						{value ?? <Spinner size="sm" />}
					</Text>
					{sub && (
						<Text fontSize="10px" color={isDark ? 'whiteAlpha.500' : 'gray.400'} mt={0.5}>
							{sub}
						</Text>
					)}
					{badge && (
						<Box mt={2} px={2} py={0.5} borderRadius="6px" bg={gradient} display="inline-block" w="fit-content">
							<Text fontSize="9px" fontWeight="700" color="white">{badge}</Text>
						</Box>
					)}
				</Flex>
				<Box
					borderRadius="12px" p={2.5} bg={gradient}
					display="flex" alignItems="center" justifyContent="center"
					className="floating-icon" ml={2} flexShrink={0}
				>
					<IconComp size={18} color="white" />
				</Box>
			</Flex>
		</Box>
	);
	if (tooltip) return (
		<ChakraTooltip label={tooltip} placement="top" hasArrow bg="gray.800" color="white" fontSize="xs" maxW="220px" px={3} py={2} borderRadius="lg">
			{card}
		</ChakraTooltip>
	);
	return card;
};

const SectionBadge = ({ children, icon: I }) => (
	<Flex align="center" gap={2} mb={4}>
		<Box p={1.5} borderRadius="8px" bg="linear-gradient(135deg,#7B5EA7,#5A3B8A)" display="flex">
			{I && <I size={14} color="white" />}
		</Box>
		<Text fontSize="15px" fontWeight="800" letterSpacing="-0.3px">{children}</Text>
	</Flex>
);

const Section = ({ children, isDark, ...props }) => (
	<Box
		className="animate-fade-up glass-card"
		bg={isDark ? 'rgba(26,32,44,0.7)' : 'rgba(255,255,255,0.9)'}
		borderRadius="16px"
		p={{ base: 4, md: 5 }}
		border="1px solid"
		borderColor={isDark ? 'whiteAlpha.100' : 'gray.100'}
		boxShadow={isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)'}
		{...props}
	>
		{children}
	</Box>
);

function Dashboard() {
	const { t } = useTranslation();
	const { user, setUser, setToken, currentBusiness } = useStateContext()
	const navigate = useNavigate()
	const isRestricted = user?.role === 'RespoSuiviClient' || user?.role === 'StockManagerClient';
	const isDark = useColorModeValue(false, true);

	useEffect(() => {
		if (user && user.role) {
			if (user.role === 'RespoSuiviClient') {
				navigate('/list_new_orders', { replace: true });
			} else if (user.role === 'StockManagerClient') {
				navigate('/New-Confirmed-Orders', { replace: true });
			}
		}
	}, [user, navigate]);
	const [usersOldCount, setUsersOldCount] = useState()
	const [usersNewCount, setUsersNewCount] = useState()


	const [countLivreThisMonth, setCountLivreThisMonth] = useState(0)

	const [commandesOldCount, setCommandesOldCount] = useState()
	const [commandesNewCount, setCommandesNewCount] = useState()

	const [productsOldCount, setProductsOldCount] = useState()
	const [productsNewCount, setProductsNewCount] = useState()

	const [ticketsOldCount, setTicketsOldCount] = useState()
	const [ticketsNewCount, setTicketsNewCount] = useState()

	const [top10Products, setTop10products] = useState([])
	const [currentPage, setCurrentPage] = useState(0);
	const [currentPageTop10, setCurrentPageTop10] = useState(0);

	const [paginationData, setPaginationData] = useState()
	const [loadingData, setLoadingData] = useState(true)
	const [checkLengthData, setCheckLengthData] = useState(false)
	const [commandes, setCommandes] = useState([])

	const [countNew, setCountNew] = useState()
	const [countAnnule, setCountAnnule] = useState()
	const [countLivre, setCountLivre] = useState()
	const [countRetour, setCountRetour] = useState()

	const [revenus, setRevenus] = useState([])
	const [charges, setCharges] = useState([])
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState(currentYear);

	const toast = useToast();

	////////////////////////////////the size of the table//////////////////////////////
	const PAGE_SIZE = 4;
	const [pageSize, setPageSize] = useState(PAGE_SIZE);
	const [pageSizeTop10, setPageSizeTop10] = useState(PAGE_SIZE);
	const handlePageSizeChange = (event) => {
		const newSize = parseInt(event.target.value, 10);
		setPageSize(newSize);
		setCurrentPage(0);
	};
	//////////////////////////////////////////////////////////////////////////////////


	const fetchBarData = (userId, userRole) => {

		axiosClient.get(`/commandes/getCommandesCount/?client=${userId}&userRole=${userRole}&business=${currentBusiness?.id}`)
			.then((res) => {
				setCountNew(res.data.count_new);

				setCountLivre(res.data.count_livre);
				setCountRetour(res.data.count_retourne);
				setCountAnnule(res.data.count_annule);

				if (res.data.error === 'Client is disabled.' && res.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
				}
			})
			.catch((error) => {
				// // console.error(error);
			});

	}

	// const fetchRevenusAndCharges = () => {
	// 	axiosClient.get('/commandes/getRevenusByMonth/')
	// 		.then((res) => {
	// 			setRevenus(res.data);
	// 		})
	// 		.catch((error) => {
	// 			// console.error(error);
	// 		});

	// 	axiosClient.get(`/charges/getChargesByMonth/?client=${user.id}`)
	// 		.then((res) => {
	// 			setCharges(res.data);
	// 		})
	// 		.catch((error) => {
	// 			// console.error(error);
	// 		});

	// }


	const labels = [t("Nouveau"), t("Livrée"), t("Retournée"), t("Annulée")];

	const dataChart = {
		labels: labels,
		datasets: [
			{
				label: "",
				data: [countNew, countLivre, countRetour, countAnnule],
				backgroundColor: [
					"rgba(54, 162, 235, 0.2)",
					"rgba(153, 102, 255, 0.2)",
					"rgba(246, 173, 85, 0.2)",
					"rgba(255, 99, 132, 0.2)",
				],
				borderColor: [
					"rgb(54, 162, 235)",
					"rgba(153, 102, 255)",
					"rgba(246, 173, 85)",
					"rgb(255, 99, 132)",

				],
				borderWidth: 1,
			},
		],
	};
	const options = {
		plugins: {
			title: {
				display: true,
				text: t("Graphique des commandes"),
				font: {
					size: 16,
				},
			},
			legend: {
				display: false
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						let label = context.dataset.label || '';
						if (label) {
							label += ': ';
						}
						if (context.parsed.y !== null) {
							label += context.parsed.y.toFixed(2);
						}
						return label;
					}
				}
			}
		},
		scales: {
			y: {
				ticks: {
					callback: function (value) {
						return value.toFixed(2);
					}
				}
			}
		}
	};


	const lineChartDataDashboard = async () => {
		const qp = (extra = {}) => {
			const base = {
				client: user?.id,
				userRole: user?.role || user?.role_client,
				business: currentBusiness?.id,
				convert_currency: true,
				year
			};
			return new URLSearchParams({ ...base, ...extra }).toString();
		};

		try {
			const [revRes, charRes] = await Promise.all([
				axiosClient.get(`/commandes/getRevenusByMonth/?${qp()}`),
				axiosClient.get(`/charges/getChargesByMonth/?${qp()}`)
			]);

			const checkDisabled = (res) => {
				if (res.data && res.data.error === 'Client is disabled.' && res.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return true;
				}
				return false;
			};

			if (checkDisabled(revRes) || checkDisabled(charRes)) {
				return null;
			}

			const revenusData = Array.isArray(revRes.data) ? revRes.data.map(val => parseFloat(val) || 0) : [];
			const chargesData = Array.isArray(charRes.data) ? charRes.data.map(val => parseFloat(val) || 0) : [];

			if (revenusData.length > 0 || chargesData.length > 0) {
				return [
					{
						name: t("Revenus"),
						data: revenusData,
					},
					{
						name: t("Charges"),
						data: chargesData,
					},
				];
			}
			return null;
		} catch (error) {
			return null;
		}
	};

	const lineChartOptionsDashboard = {
		chart: { toolbar: { show: false } },
		tooltip: { theme: 'dark', y: { formatter: v => formatNumber(v) } },
		dataLabels: { enabled: false },
		stroke: { curve: 'smooth', width: 3 },
		xaxis: {
			type: "category",
			categories: [t('Jan'), t('Fév'), t('Mar'), t('Avr'), t('Mai'), t('Jui'), t('Juil'), t('Aou'), t('Sep'), t('Oct'), t('Nov'), t('Dec')],
			labels: { style: { colors: isDark ? '#718096' : '#718096', fontSize: '11px', fontFamily: 'Inter' } },
			axisBorder: { show: false }, axisTicks: { show: false }
		},
		yaxis: { labels: { style: { colors: isDark ? '#718096' : '#718096', fontSize: '11px', fontFamily: 'Inter' }, formatter: v => formatNumber(v) } },
		legend: { show: false },
		grid: { strokeDashArray: 5, borderColor: isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.07)' },
		fill: { type: 'gradient', gradient: { shade: 'dark', type: 'vertical', opacityFrom: 0.7, opacityTo: 0 }, colors: ['#6C63FF', '#FF6584'] },
		colors: ['#6C63FF', '#FF6584'],
	};

	const fetchData = () => {
		axiosClient.get(`count_users/${user.id}/?business=${currentBusiness?.id}`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return;
				}

				setUsersOldCount(response.data.old_count);
				setUsersNewCount(response.data.new_count);
			}).catch((error) => {
				console.error("Background stat fetch failed:", error);
			});

		axiosClient.get(`count_commandes/${user.id}/?business=${currentBusiness?.id}`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return;
				}

				setCommandesOldCount(response.data.old_count);
				setCommandesNewCount(response.data.new_count);
			})
			.catch((error) => {
				// console.log(error);
			});

		axiosClient.get(`count_products/${user.id}/?business=${currentBusiness?.id}`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return;
				}

				setProductsOldCount(response.data.old_count);
				setProductsNewCount(response.data.new_count);
			})
			.catch((error) => {
				// console.log(error);
			});
		axiosClient.get(`count_tickets/${user.id}/`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return;
				}

				setTicketsOldCount(response.data.old_count);
				setTicketsNewCount(response.data.new_count);
			})
			.catch((error) => {
				// console.log(error);
			});
	}
	const fetchCommandes = () => {
		setLoadingData(true)
		axiosClient.get(`/commandes/fetch_month_commandes/?page_size=${pageSize}&page=${currentPage + 1}&client=${user.id}&business=${currentBusiness?.id}`)
			.then((response) => {
				let filteredCmd = response.data.results || []
				setPaginationData(response.data);

				if (filteredCmd.length === 0) {
					setCheckLengthData(true)
				}
				else {
					setCommandes(filteredCmd);
				}
				setLoadingData(false)

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
				}
			})
			.catch((error) => {
				// // console.error(error);
				setLoadingData(false)
			});
		axiosClient.get(`count_commandes_livre_this_month/${user.id}/?business=${currentBusiness?.id}`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return
				}

				setCountLivreThisMonth(response.data.count);
			})
			.catch((error) => {
				// console.log(error);
			});

	}
	const fetchTop10Products = () => {
		axiosClient.get(`top_products/${user.id}/?business=${currentBusiness?.id}`)
			.then((response) => {

				if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
					setUser({});
					setToken(null);
					localStorage.removeItem("tokenExpirationTime");
					localStorage.removeItem("refreshTimeAdmin");
					localStorage.removeItem("role");

					// Vérifier si le toast n'est pas déjà affiché
					if (!toast.isActive('accountDisabled')) {
						toast({
							id: 'accountDisabled',
							description: t("le compte est désactivé"),
							status: 'warning',
							variant: 'left-accent',
							duration: 2000,
							isClosable: true,
							position: 'bottom-right'
						});
					}
					return;
				}

				setTop10products(response.data);
			})
			.catch((error) => {
				// console.log(error);
			});


	}

	const [lineChartData, setLineChartData] = useState(null);

	useEffect(() => {
		if (user?.id && !isRestricted) {
			const fetchData = async () => {
				try {
					const data = await lineChartDataDashboard();
					setLineChartData(data);
				} catch (error) {
					// // console.error('Error fetching data:', error);
				}
			};

			fetchData();
		}
	}, [user?.id, currentBusiness?.id, isRestricted, year]);

	useEffect(() => {
		if (user?.id && !isRestricted) {
			fetchData()
			fetchTop10Products()
			fetchBarData(user?.id, user?.role)
		}
	}, [user?.id, currentBusiness?.id, isRestricted]);

	useEffect(() => {
		if (user?.id && !isRestricted) {
			fetchCommandes()
		}
	}, [user?.id, currentBusiness?.id, currentPage, pageSize, isRestricted]);

	useEffect(() => {
		setCurrentPage(0);
		setCurrentPageTop10(0);
	}, [currentBusiness?.id]);



	if (!user || !user.role) {
		return (
			<Flex justify="center" align="center" minH="50vh" mt={10}>
				<Spinner size="xl" color="purple.500" thickness="4px" />
			</Flex>
		);
	}

	if (isRestricted) {
		return null;
	}

	return (
		<Flex mt={2} flexDirection='column' className="stats-root" gap={0}>
			<style>{GLOBAL_CSS}</style>

			<SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing='24px' mb="24px">
				<GlassMetricCard
					label={t("Nombre de personnels")}
					value={<FormattedNumber value={usersOldCount} isCurrency={false} />}
					sub={usersNewCount > 0 ? `+ ${usersNewCount} ${t("nouveaux")}` : ''}
					icon={FaUserGroup}
					gradient="linear-gradient(135deg, #38B2AC, #319795)"
					isDark={isDark}
					delay={100}
				/>
				<GlassMetricCard
					label={t("Nombre de commandes")}
					value={<FormattedNumber value={commandesOldCount} isCurrency={false} />}
					sub={commandesNewCount > 0 ? `+ ${commandesNewCount} ${t("nouvelles")}` : ''}
					icon={FaUserTie}
					gradient="linear-gradient(135deg, #F56565, #C53030)"
					isDark={isDark}
					delay={200}
				/>
				<GlassMetricCard
					label={t("Nombre de produits")}
					value={<FormattedNumber value={productsOldCount} isCurrency={false} />}
					sub={productsNewCount > 0 ? `+ ${productsNewCount} ${t("nouveaux")}` : ''}
					icon={IoDocument}
					gradient="linear-gradient(135deg, #4299E1, #2B6CB0)"
					isDark={isDark}
					delay={300}
				/>
				<GlassMetricCard
					label={t("Réclamations")}
					value={<FormattedNumber value={ticketsOldCount} isCurrency={false} />}
					sub={ticketsNewCount > 0 ? `+ ${ticketsNewCount} ${t("nouvelles")}` : ''}
					icon={BsPersonVcardFill}
					gradient="linear-gradient(135deg, #ECC94B, #D69E2E)"
					isDark={isDark}
					delay={400}
				/>
			</SimpleGrid>
			<Grid
				templateColumns={{ sm: '1fr', lg: '1.7fr 1.3fr' }}
				maxW={{ sm: '100%', md: '100%' }}
				gap='24px'
				mb='24px'>

				{/* Sales Overview */}
				<Section isDark={isDark} p={4} overflow="hidden" display="flex" flexDirection="column">
					<Flex justify="space-between" align="center" mb={2}>
						<SectionBadge icon={MdTrendingUp}>{t("Évolution des Revenus et des Charges")}</SectionBadge>
						<Select size="xs" w="90px" value={year} borderRadius="6px"
							onChange={e => setYear(e.target.value === '0' ? currentYear : e.target.value)}>
							<option value="0">{t('années')}</option>
							{Array.from({ length: currentYear - 2023 }, (_, i) => 2024 + i).map(y => (
								<option key={y} value={y}>{y}</option>
							))}
						</Select>
					</Flex>
					<Box w='100%' flex="1" minH={{ base: '300px', sm: "400px" }} pt={2} px={0} pb={0}>
						{lineChartData && lineChartData.length > 0 ? (
							<LineChart
								lineChartData={lineChartData}
								lineChartOptions={lineChartOptionsDashboard}
							/>
						) : (
							<Box display="flex" h="100%" minH="300px" alignItems="center" justifyContent="center" color="gray.400">
								<Text fontSize="12px">{t('Aucune donnée de revenu ou de charge disponible')}</Text>
							</Box>
						)}
					</Box>
					{/* Legend */}
					{lineChartData && lineChartData.length > 0 && (
						<Flex gap={4} mt={3} justify="center">
							{[{ color: '#6C63FF', label: t('Revenus') }, { color: '#FF6584', label: t('Charges') }].map(l => (
								<Flex key={l.label} align="center" gap={1.5}>
									<Box w="8px" h="8px" borderRadius="50%" bg={l.color} />
									<Text fontSize="10px" fontWeight="600" color="gray.500">{l.label}</Text>
								</Flex>
							))}
						</Flex>
					)}
				</Section>

				{/* Order Status */}
				<Section isDark={isDark} p={0} overflow="hidden" display="flex" flexDirection="column">
					<Box p={{ base: 4, md: 6 }} pb={0}>
						<SectionBadge icon={MdOutlineBarChart}>{t("Statuts de commandes")}</SectionBadge>
					</Box>
					<Flex direction='column' w='100%' flex="1">
						<Box
							w='100%'
							flex="1"
							display={{ sm: 'flex', md: 'block' }}
							justify={{ sm: 'center', md: 'flex-start' }}
							align={{ sm: 'center', md: 'flex-start' }}
							minH={{ base: '180px', md: '200px' }}
							px={0} pt={2} pb={2}
							borderBottom="1px solid" borderColor={isDark ? 'whiteAlpha.100' : 'blackAlpha.50'}>
							<Bar data={dataChart} options={options} />
						</Box>

						<SimpleGrid gap={4} columns={{ base: 2, md: 4 }} p={{ base: 4, md: 6 }}>
							<Flex direction='column'>
								<Flex alignItems='center' mb={2}>
									<Box p={1.5} borderRadius="8px" bg="blue.50" me={2}>
										<MdOutlineAddShoppingCart size={16} color="#3182ce" />
									</Box>
									<Text fontSize='xs' fontWeight="600" color='gray.400' textTransform="uppercase">{t("Nouveau")}</Text>
								</Flex>
								<Text fontSize='lg' fontWeight='800' mb={2} color={isDark ? 'white' : 'gray.700'}>{countNew}</Text>
								<Progress colorScheme='blue' borderRadius='30px' h='4px' value={20} />
							</Flex>

							<Flex direction='column'>
								<Flex alignItems='center' mb={2}>
									<Box p={1.5} borderRadius="8px" bg="purple.50" me={2}>
										<TbTruckDelivery size={16} color="#805ad5" />
									</Box>
									<Text fontSize='xs' fontWeight="600" color='gray.400' textTransform="uppercase">{t("Livrée")}</Text>
								</Flex>
								<Text fontSize='lg' fontWeight='800' mb={2} color={isDark ? 'white' : 'gray.700'}>{countLivre}</Text>
								<Progress colorScheme='purple' borderRadius='30px' h='4px' value={90} />
							</Flex>

							<Flex direction='column'>
								<Flex alignItems='center' mb={2}>
									<Box p={1.5} borderRadius="8px" bg="orange.50" me={2}>
										<TbTruckReturn size={16} color="#dd6b20" />
									</Box>
									<Text fontSize='xs' fontWeight="600" color='gray.400' textTransform="uppercase">{t("Retournée")}</Text>
								</Flex>
								<Text fontSize='lg' fontWeight='800' mb={2} color={isDark ? 'white' : 'gray.700'}>{countRetour}</Text>
								<Progress colorScheme='orange' borderRadius='30px' h='4px' value={30} />
							</Flex>

							<Flex direction='column'>
								<Flex alignItems='center' mb={2}>
									<Box p={1.5} borderRadius="8px" bg="red.50" me={2}>
										<LuPackageX size={16} color="#e53e3e" />
									</Box>
									<Text fontSize='xs' fontWeight="600" color='gray.400' textTransform="uppercase">{t("Annulée")}</Text>
								</Flex>
								<Text fontSize='lg' fontWeight='800' mb={2} color={isDark ? 'white' : 'gray.700'}>{countAnnule}</Text>
								<Progress colorScheme='red' borderRadius='30px' h='4px' value={50} />
							</Flex>
						</SimpleGrid>
					</Flex>
				</Section>
			</Grid>

			<Grid templateColumns={{ sm: '1fr', md: '1fr', lg: '2fr 1fr' }} gap='24px'>
				{/* Projects */}
				<Section isDark={isDark} p={0} overflow="hidden">
					<Box p={{ base: 4, md: 6 }} pb={0}>
						<SectionBadge icon={IoCheckmarkDoneCircleSharp}>{t("Les commandes livrées")}</SectionBadge>
						<Text fontSize='13px' color='gray.400' fontWeight='normal' mt="-8px" mb="16px" ml="34px">
							<Text fontWeight='bold' as='span' color='teal.400'>
								{countLivreThisMonth} {t("Livrées")}
							</Text>{' '}
							{t("ce mois.")}
						</Text>
					</Box>
					<TableContainer pb={5} px={{ base: 2, md: 4 }}>
						<Table size={"sm"} variant='simple'>
							<Thead>
								<Tr my='.8rem' ps='0px'>
									<Th fontSize={"10px"} color='gray.500'>
										{t("Code commande")}
									</Th>
									<Th fontSize={"10px"} color='gray.500'>
										{t("Client")}
									</Th>
									<Th fontSize={"10px"} color='gray.500'>
										{t("Gestionnaire")}
									</Th>
									<Th fontSize={"10px"} color='gray.500'>
										{t("Prix")}
									</Th>
								</Tr>
							</Thead>
							<Tbody>
								{loadingData &&
									<Tr>
										<Td><Skeleton height='20px' /></Td>
										<Td><Skeleton height='20px' /></Td>
										<Td><Skeleton height='20px' /></Td>
										<Td><Skeleton height='20px' /></Td>
									</Tr>}
								{(paginationData?.length !== 0) && commandes.map((row, idx) =>
									<Tr key={idx} my='.8rem' ps='0px' _hover={{ bg: isDark ? 'whiteAlpha.50' : 'gray.50' }} transition="all .2s">
										<Td fontSize="13px" fontWeight="600">{row.code}</Td>
										<Td>
											<Flex gap={1} direction='column'>
												<Text fontSize='13px' fontWeight='600' color={isDark ? 'white' : 'gray.800'}>
													{row.full_name}
												</Text>
												<Text fontSize='11px' color='gray.500'>
													{row.tel}
												</Text>
											</Flex>
										</Td>
										<Td>
											<Flex gap={3} align={"center"}>
												<Avatar size={"sm"} name={row.gst_first_name} src={row.image} />
												<Flex gap={0} direction={"column"}>
													<Text fontSize="13px" fontWeight="600" color={isDark ? 'white' : 'gray.800'}>{row.gst_first_name}</Text>
													<Text fontSize="11px" color='gray.500'>{row.gst_last_name}</Text>
												</Flex>
											</Flex>
										</Td>
										<Td>
											<Flex gap={1} align="center">
												<Text fontWeight={"700"} color={isDark ? 'white' : 'gray.800'}>
													<FormattedNumber value={row.prix} isCurrency={false} />
												</Text>
												<Text fontSize={11} color="gray.500" mt={0.5}>{currentBusiness?.currency}</Text>
											</Flex>
										</Td>
									</Tr>
								)}
								{(commandes?.length === 0) && (
									<Tr>
										<Td colSpan={4} textAlign="center" py={4} color="gray.500">{t("il n'y a pas encore de données.")}</Td>
									</Tr>
								)}
							</Tbody>
						</Table>
					</TableContainer>
					{commandes?.length > 0 &&
						<Flex justify="space-between" align="center" w="100%" p={4}>
							<Box flex="1">
								<MyPagination
									paginationData={paginationData}
									currentPage={currentPage}
									setCurrentPage={setCurrentPage}
									PAGE_SIZE={pageSize}
								/>
							</Box>
						</Flex>
					}
				</Section>

				{/* Orders Overview */}
				<Section isDark={isDark} p={0} overflow="hidden">
					<Box p={{ base: 4, md: 6 }} pb={0}>
						<SectionBadge icon={IoCheckmarkDoneCircleSharp}>{t("Top 10 produits")}</SectionBadge>
						<Text fontSize='13px' color='gray.400' fontWeight='normal' mt="-8px" mb="16px" ml="34px">
							<Text fontWeight='bold' as='span' color='teal.400'>
								{t("basé sur les produits livrés")}
							</Text>
						</Text>
					</Box>
					<Box px={{ base: 4, md: 6 }} pb={6}>
						{top10Products.slice(currentPageTop10 * pageSizeTop10, currentPageTop10 * pageSizeTop10 + pageSizeTop10).map((row, idx) => (
							<Flex key={idx} cursor={"pointer"} transition={"transform .3s"} mb={3}
								bg={isDark ? 'whiteAlpha.50' : 'gray.50'}
								border="1px solid" borderColor={isDark ? 'whiteAlpha.100' : 'gray.100'}
								borderRadius="12px" p={3}
								_hover={{ shadow: "md", transform: "translateY(-3px)", borderColor: "purple.300" }}
								alignItems="center" justifyContent="space-between">
								<Flex align={"center"} gap={3}>
									<Avatar borderRadius={"8px"} size="sm" name={row.title} src={row?.image ? (row.image.startsWith('http') ? row.image : linkImage + row.image) : undefined} />
									<Flex direction={"column"}>
										<Text fontSize="13px" fontWeight="600" color={isDark ? 'white' : 'gray.800'}>{row.title}</Text>
										<Text color='gray.500' fontSize={"11px"}>{row.code}</Text>
									</Flex>
								</Flex>
								<Box px={2.5} py={1} borderRadius="6px" bg="purple.500" color="white" fontSize="12px" fontWeight="700">
									{row.count}
								</Box>
							</Flex>
						))}

						{top10Products?.length > 0 ?
							<Flex justify="space-between" align="center" w="100%" mt={4}>
								<Box flex="1">
									<SimplePagination
										data={top10Products}
										PAGE_SIZE={pageSizeTop10}
										currentPage={currentPageTop10}
										setCurrentPage={setCurrentPageTop10}
									/>
								</Box>
							</Flex>
							:
							<Flex my={'5'} align={'center'} justify={'center'}>
								<Image opacity={0.1} height={100} width={150} rounded={'md'} alt={'empty'} src={empty} objectFit={'contain'} />
							</Flex>
						}
					</Box>
				</Section>
			</Grid>
		</Flex>
	)
}

export default Dashboard